import { DexQueryView, DexRegionEnum, DexRegionEnumType, DexResultsView } from '@mark43/dex-api';
import {
    Box,
    Card,
    CardHeader,
    MenuList,
    Text,
    Caution,
    HStack,
    Icon,
    VStack,
} from '@cad-arc';
import { format } from 'date-fns';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useOpenEvent } from '../../common/hooks/use-open-event';
import { FR_EVENT_DETAILS_TABS } from '../../fr-event-details-page/constants';
import { getArcHitColor, getIconForDexEntityType } from '../utils';
import { DexResultItems } from './dex-result-items';
import { LinkedEventNumber } from './dex-results-linked-event';
import { resultsHaveHit } from './utils';
import { Tooltip } from '~/common/components/tooltip';

const getShouldShowQuerySummaryInHeader = (dexRegion?: DexRegionEnumType) => {
    return dexRegion !== DexRegionEnum.MA_LEAPS.name;
};

export function ResultsCard({
    dexResultsView,
    selectedResultId,
    selectedSummaryId,
}: Readonly<{
    dexResultsView: DexResultsView;
    selectedResultId?: number;
    selectedSummaryId?: number;
}>): React.ReactElement {
    const { query, results, resultsSummaryView, cascadedQueries } = dexResultsView;
    const { dexRegion, userProfileView } = useBootstrapState();
    const showQuerySummaryInHeader = getShouldShowQuerySummaryInHeader(dexRegion);
    const { userId } = userProfileView;
    const hasHits = resultsHaveHit(results ?? []);

    if (showQuerySummaryInHeader) {
        return (
            <Card
                key={query.outgoingMessageId}
                style={{
                    width: '100%',
                    backgroundColor: 'var(--arc-colors-surface-accent)',
                }}
            >
                {results.length > 0 ? (
                    <>
                        <CardHeader>
                            <QueryHeader
                                query={query}
                                userId={userId}
                                showQuerySummaryInHeader
                                hasHits={hasHits}
                            />
                        </CardHeader>
                        <MenuList>
                            <DexResultItems
                                outgoingMessageId={query.outgoingMessageId}
                                results={results}
                                resultsSummaryView={resultsSummaryView}
                                selectedResultId={selectedResultId}
                                selectedSummaryId={selectedSummaryId}
                                extraPaddingLeft="0px"
                            />
                        </MenuList>
                    </>
                ) : (
                    <Box padding="var(--arc-space-2) var(--arc-space-4)">
                        <QueryHeader
                            query={query}
                            userId={userId}
                            hasHits={hasHits}
                            showQuerySummaryInHeader
                        />
                    </Box>
                )}
            </Card>
        );
    }

    return (
        <Card
            key={query.outgoingMessageId}
            style={{
                width: '100%',
                backgroundColor: 'var(--arc-colors-surface-accent)',
            }}
        >
            <>
                <Box padding="8px 8px 0 8px">
                    <QueryHeader
                        query={query}
                        userId={userId}
                        hasHits={hasHits}
                        showQuerySummaryInHeader={false}
                    />
                </Box>
                <Box padding="0 8px 8px 8px">
                    <DexResultItems
                        query={query}
                        outgoingMessageId={query.outgoingMessageId}
                        results={results}
                        resultsSummaryView={resultsSummaryView}
                        selectedResultId={selectedResultId}
                        selectedSummaryId={selectedSummaryId}
                        cascadedQueries={cascadedQueries}
                        extraPaddingLeft="8px"
                    />
                </Box>
            </>
        </Card>
    );
}

function QueryHeader({
    query,
    userId,
    hasHits,
    showQuerySummaryInHeader,
}: {
    query: DexQueryView;
    userId: number;
    hasHits: boolean;
    showQuerySummaryInHeader?: boolean;
}): React.ReactElement {
    const handleEventLinkClick = useOpenEvent();

    const isQueryByCurrentUser = query.createdBy === userId;
    const formattedQueryCreatedDate = format(new Date(query.createdDateUtc), 'MM/dd/y HH:mm:ss');
    const formattedDateAndUnit = `${formattedQueryCreatedDate}${
        query.requestingUnitCallSign && isQueryByCurrentUser
            ? ` • Unit ${query.requestingUnitCallSign}`
            : ''
    }`;

    if (showQuerySummaryInHeader) {
        return (
            <HStack spacing={2}>
                <Icon
                    icon={getIconForDexEntityType(query.dexEntityType)}
                    color={hasHits ? 'raw.red.500' : 'icon.default'}
                />
                <VStack alignItems="flex-start" spacing={0} w="100%">
                    <Text
                        noOfLines={1}
                        variant="bodyMd"
                        style={{ color: getArcHitColor(hasHits), wordBreak: 'break-all' }}
                        fontWeight={hasHits ? 'bold' : undefined}
                    >
                        {query.formattedQuery}
                    </Text>
                    <HStack justifyContent="space-between" w="100%">
                        <Text variant="caption" color="secondary">
                            {formattedDateAndUnit}
                        </Text>
                        {query.requestingUnitCallSign && !isQueryByCurrentUser ? (
                            <Tooltip overlay={`Run by ${query.requestingUnitCallSign}`}>
                                <Caution priority={4} size="sm">
                                    Unit {query.requestingUnitCallSign}
                                </Caution>
                            </Tooltip>
                        ) : undefined}
                    </HStack>
                    {query.agencyEventNumber && query.agencyEventId ? (
                        <LinkedEventNumber
                            onClick={() =>
                                handleEventLinkClick({
                                    eventId: query.agencyEventId,
                                    frEventDetailsTabName: FR_EVENT_DETAILS_TABS.NARRATIVE,
                                })
                            }
                        >
                            #{query.agencyEventNumber}
                        </LinkedEventNumber>
                    ) : undefined}
                </VStack>
                {hasHits && <Icon icon="ExclamationPoint" color="raw.red.500" />}
            </HStack>
        );
    }

    return (
        <HStack spacing={1}>
            <VStack alignItems="flex-start" spacing={0} w="100%">
                {query.agencyEventNumber && query.agencyEventId ? (
                    <Box>
                        <LinkedEventNumber
                            onClick={() =>
                                handleEventLinkClick({
                                    eventId: query.agencyEventId,
                                    frEventDetailsTabName: FR_EVENT_DETAILS_TABS.NARRATIVE,
                                })
                            }
                        >
                            #{query.agencyEventNumber}
                        </LinkedEventNumber>
                    </Box>
                ) : undefined}
                <HStack justifyContent="space-between" w="100%" paddingBottom="var(--arc-space-1)">
                    <Text variant="caption" color="secondary">
                        {formattedDateAndUnit}
                    </Text>
                    {query.requestingUnitCallSign && !isQueryByCurrentUser ? (
                        <Tooltip overlay={`Run by ${query.requestingUnitCallSign}`}>
                            <Caution priority={4} size="sm">
                                Unit {query.requestingUnitCallSign}
                            </Caution>
                        </Tooltip>
                    ) : undefined}
                </HStack>
            </VStack>
        </HStack>
    );
}
